import {
	createComponentsRegistrySiteAssets,
	IComponentsRegistrySiteAssets,
	ILibraryTopology,
} from '@wix/thunderbolt-components-registry/siteAssets'

import type { FetchFn, MetricsReporter } from '@wix/thunderbolt-becky-types'
import type { ComponentsNativeMappers } from '@wix/thunderbolt-becky-root'
import { InvalidJsonError } from '../utils/errors'

type SkinParams = Partial<Record<string, any>>

// Cache component mappers per topology to avoid loading them on each request
const componentMappersCache = new Map<string, Promise<ComponentsNativeMappers>>()
const componentsSkinParamsCache = new Map<string, Promise<SkinParams>>()

function getLibraries(registryLibrariesTopology: string): Array<ILibraryTopology> {
	if (!registryLibrariesTopology) {
		return []
	}
	try {
		return JSON.parse(registryLibrariesTopology)
	} catch (error) {
		throw new InvalidJsonError('registryLibrariesTopology', error)
	}
}

async function getWrappedCachedRegistry({
	registryLibrariesTopology,
	fetchFn,
	metricsReporter,
	isExperimentOpen,
	isPlatformBuilderModule = false,
	isEditor,
}: {
	registryLibrariesTopology: string
	fetchFn: FetchFn
	metricsReporter: MetricsReporter
	isExperimentOpen: (specName: string) => boolean
	isPlatformBuilderModule?: boolean
	isEditor: boolean
}): Promise<IComponentsRegistrySiteAssets> {
	const libraries = getLibraries(registryLibrariesTopology)
	const registry = await createComponentsRegistrySiteAssets({
		libraries,
		modulesLoader: {
			relativeUrl: () => {
				throw new Error('ModulesLoader `relativeUrl` method should not be used')
			},
			absoluteUrl: async (url) => {
				const response = await fetchFn(url)
				return response.text() as any
			},
		},
		runAndReport: (metric, fn) => {
			return metricsReporter.runAsyncAndReport(fn, metric)
		},
		isExperimentOpen,
		isPlatformBuilderModule,
		isEditor,
	})

	return registry
}

export const getComponentMappers = async ({
	fetchFn,
	registryLibrariesTopology,
	metricsReporter,
	isExperimentOpen,
	isPlatformBuilderModule = false,
	isEditor,
}: {
	fetchFn: FetchFn
	registryLibrariesTopology: string
	metricsReporter: MetricsReporter
	isExperimentOpen: (specName: string) => boolean
	isPlatformBuilderModule?: boolean
	isEditor: boolean
}): Promise<ComponentsNativeMappers> => {
	if (componentMappersCache.has(registryLibrariesTopology)) {
		return componentMappersCache.get(registryLibrariesTopology)!
	}
	const registry = await getWrappedCachedRegistry({
		registryLibrariesTopology,
		fetchFn,
		metricsReporter,
		isExperimentOpen,
		isPlatformBuilderModule,
		isEditor,
	})

	const componentMappers = registry.loadComponentMappers() as Promise<ComponentsNativeMappers>

	if (process.env.NODE_ENV === 'production') {
		componentMappersCache.set(registryLibrariesTopology, componentMappers)
	}

	return componentMappers
}

export const getComponentSkinParams = async ({
	fetchFn,
	registryLibrariesTopology,
	metricsReporter,
	isExperimentOpen,
	isPlatformBuilderModule = false,
	isEditor,
}: {
	fetchFn: FetchFn
	registryLibrariesTopology: string
	metricsReporter: MetricsReporter
	isExperimentOpen: (specName: string) => boolean
	isPlatformBuilderModule?: boolean
	isEditor: boolean
}): Promise<SkinParams> => {
	if (componentsSkinParamsCache.has(registryLibrariesTopology)) {
		return componentsSkinParamsCache.get(registryLibrariesTopology)!
	}

	const registry = await getWrappedCachedRegistry({
		registryLibrariesTopology,
		fetchFn,
		metricsReporter,
		isExperimentOpen,
		isPlatformBuilderModule,
		isEditor,
	})

	const componentsSkinParams = registry.loadSkinParams() as Promise<SkinParams>

	if (process.env.NODE_ENV === 'production') {
		componentsSkinParamsCache.set(registryLibrariesTopology, componentsSkinParams)
	}

	return componentsSkinParams
}
